/**
 * A CONTA DE VARREDURA (ContaMax do Santander) no painel da aplicação (13/10/2026, backend #440).
 *
 * Conta de varredura não tem lote: o saldo vem das linhas da corrente (o `fundSweep` do backend), e a diferença contra a
 * posição do banco é o RENDIMENTO que ficou no fundo, não erro. Antes da #440 o painel mostrava o `totalGross` dos lotes
 * (zero, porque não há lote) e o `drift` antigo (lotes − posição) acusava como "aplicação não registrada" o saldo inteiro.
 *
 * Com a #440 o resumo traz o drift PARTIDO e o saldo a mostrar:
 *  - `saldoMostrado` + `origemDoSaldo` ('posicao' | 'derivado' | 'indisponivel') + `dataDaPosicao` + `idadeDaPosicaoEmDias`;
 *  - `rendimentoDeclarado`: o rendimento retido que a posição revela (pode ser NEGATIVO: rendimento já resgatado);
 *  - `driftInexplicado`: o que sobra sem explicação; qualquer valor ≠ 0 é ALERTA;
 *  - `avisoDaPosicao` (opcional): posição velha ou ausente.
 *
 * ⛔ Compatível: o backend sem a #440 não manda as chaves, e aí esta leitura devolve `null` e o painel fica como era.
 * "Chave ausente não é null": a detecção é pela PRESENÇA de `origemDoSaldo`, não pelo valor.
 * ⛔ Só conta de varredura (sem lotes, o mesmo critério do backend). O fundo COM lotes (Sicredi) segue no caminho do
 * `drift` antigo: lá a diferença continua sendo movimentação não registrada.
 */
export type OrigemDoSaldo = 'posicao' | 'derivado' | 'indisponivel';

export interface LeituraDaVarredura {
  saldo: number | null;
  origem: OrigemDoSaldo;
  dataDaPosicao: string | null;
  idadeDaPosicaoEmDias: number | null;
  rendimentoDeclarado: number | null;
  /** `null` quando não há posição para comparar; 0 quando tudo se explica. */
  driftInexplicado: number | null;
  aviso: string | null;
}

const num = (v: any): number | null => (v == null || !Number.isFinite(Number(v)) ? null : Number(v));

export function lerVarredura(summary: any): LeituraDaVarredura | null {
  if (!summary || typeof summary !== 'object') return null;
  if (!('origemDoSaldo' in summary)) return null; // backend sem a #440
  const lotes = Array.isArray(summary.lots) ? summary.lots : [];
  if (lotes.length > 0) return null; // fundo com lotes: não é varredura
  const origem: OrigemDoSaldo = summary.origemDoSaldo === 'posicao' || summary.origemDoSaldo === 'derivado' ? summary.origemDoSaldo : 'indisponivel';
  return {
    saldo: num(summary.saldoMostrado),
    origem,
    dataDaPosicao: typeof summary.dataDaPosicao === 'string' ? summary.dataDaPosicao : null,
    idadeDaPosicaoEmDias: num(summary.idadeDaPosicaoEmDias),
    rendimentoDeclarado: num(summary.rendimentoDeclarado),
    driftInexplicado: num(summary.driftInexplicado),
    aviso: typeof summary.avisoDaPosicao === 'string' && summary.avisoDaPosicao.trim() ? summary.avisoDaPosicao : null,
  };
}

/** O drift inexplicado é alerta em qualquer valor diferente de zero (compara em centavos, como o backend). */
export function temDriftInexplicado(v: LeituraDaVarredura | null): boolean {
  return v != null && v.driftInexplicado != null && Math.round(v.driftInexplicado * 100) !== 0;
}

/**
 * A frase que vai sob o saldo. Saldo DERIVADO diz de onde vem: as aplicações e resgates automáticos da corrente (14/10/2026,
 * backend #448). Antes dizia "não inclui o rendimento retido no fundo. Importe ou informe a posição do banco", e era
 * enganoso: a diferença que parecia rendimento retido era o saldo de ABERTURA do fundo, que a #448 passou a somar
 * (`initialBalance`), e a separação roda sozinha ao fim de todo import. Com posição, a data anda junto do saldo. Sem os
 * dois, diz que não há saldo.
 */
export function fraseDoSaldoDaVarredura(v: LeituraDaVarredura, dataBR: (d: string) => string): string {
  if (v.origem === 'derivado') {
    return 'Calculado pelas aplicações e resgates automáticos da conta corrente (OFX). Atualiza a cada importação.';
  }
  if (v.origem === 'indisponivel') return 'Sem posição do banco e sem saldo derivado: não há saldo para mostrar.';
  const idade = v.idadeDaPosicaoEmDias;
  const quando = idade === 0 ? ' (hoje)' : idade === 1 ? ' (ontem)' : idade != null ? ` (há ${idade} dias)` : '';
  return `Posição do banco de ${v.dataDaPosicao ? dataBR(v.dataDaPosicao) : '—'}${quando}`;
}
